import { Button, Grid, Theme, useMediaQuery } from "@mui/material"
import { push } from "connected-react-router"
import { ListContextProvider, ReactAdminComponentPropsWithId, ShowBase, useListController, useShowContext } from "ra-core"
import { FC, useCallback } from "react"
import { useDispatch } from "react-redux"
import LogViewer, { LogViewerProvider, useLogViewerController } from "~/components/backups/LogViewer"
import { Content } from "~/components/content/Content"
import DatabaseBackupSchedule from "~/components/databases/DatabaseBackupSchedule"
import DatabaseStatistics from "~/components/databases/DatabaseStatistics"
import DatabaseSummary from "~/components/databases/DatabaseSummary"
import DatabaseTipsRecommend from "~/components/databases/DatabaseTipsRecommend"
import { RecPatch } from "~/dataProvider/tools"
import { DatabaseResponseDto } from "~/gen/api/backup"
import { OdResourcePageProps } from "~/types/resource"
import { Link as RouterLink } from 'react-router-dom'
import { parse, pickQueries } from "~/utils/queryStringWrapper"
import TopIndexSizeChart from "~/components/databases/TopIndexSizeChart"
import DatabaseSizeChart from "~/components/databases/DatabaseSizeChart"
import TopRowCountChart from "~/components/databases/TopRowCountChart"
import { useResourceRegister } from "~/utils/resourceRegister"
import { MetricsContextProvider, useMetricsContext, useMetricsController } from "./useMetricsController"
import { TimeRangeType } from "~/gen/api/metrics"
import { useAllDatabasesSource, useDatabaseSelectCard } from "~/components/databases/DatabaseSelectCard"
import { useEffect } from "react"
import { decodeHashid, genUrlWithHashid } from "~/utils/hashid"
import { EncryptedDatabasesContextProvider } from "~/components/databases/useEncryptedDatabases"
import { MoreOptionsMenu } from "./DatabaseDetail.moreActions"


const DatabaseDetail: FC<ReactAdminComponentPropsWithId> = (props) => {
  useResourceRegister({ name: 'dbmetrics-dbsize' })
  useResourceRegister({ name: 'dbmetrics-indexsize' })
  useResourceRegister({ name: 'dbmetrics-rowcount' })

  const { id: hId, ...rest } = props
  const id = decodeHashid(hId || '')
  return (
    <ShowBase id={id} {...rest} mutationMode={'pessimistic'} >
      <DatabaseDetailCater {...{id}} {...props} />
    </ShowBase>
  )
}


const DatabaseDetailCater: FC<OdResourcePageProps> = (props) => {
  const { location } = props
  const { record } = useShowContext<DatabaseResponseDto & RecPatch>()
  const dispatch = useDispatch()
  
  const mode = pickQueries(parse(location?.search || ''))('mode') === 'metrics' ? 'metrics' : 'detail'

  const handleSwitchDatabase = useCallback((db: DatabaseResponseDto) => {
    dispatch(push(
      genUrlWithHashid(`/databases/$0/show${location?.search || ''}`, db.databaseId || 0, db.name)))
  }, [dispatch, location?.search])

  const metricsCtrl = useMetricsController({ preDbId: record?.databaseId })
  return (
    <MetricsContextProvider value={metricsCtrl}>
      <EncryptedDatabasesContextProvider>
        <DatabaseDetailBody 
          databaseInfo={record} 
          onSwitchDatabase={handleSwitchDatabase} 
          mode={mode}
          {...props} 
        />
      </EncryptedDatabasesContextProvider>
    </MetricsContextProvider>
  )
}

export type PageMode = 'detail' | 'metrics'

export interface DatabaseDetailBodyProps extends OdResourcePageProps {
  databaseInfo: DatabaseResponseDto | undefined;
  onSwitchDatabase: (x: DatabaseResponseDto) => void;
  mode: PageMode;
}

export const DatabaseDetailBody: FC<DatabaseDetailBodyProps> = ({
  location,
  databaseInfo,
  onSwitchDatabase,
  mode = 'detail',
  options,
  permissions = [],
  resource,
}) => {
  const DatabaseSelectCard = useDatabaseSelectCard(useAllDatabasesSource)
  
  const {
    setDbId,
    timeRange,
  } = useMetricsContext()

  const handleChange = useCallback((e: any, chosenDb?: DatabaseResponseDto) => {
    if (chosenDb?.databaseId) {
      setDbId(chosenDb.databaseId)
      onSwitchDatabase(chosenDb)
    }
  }, [setDbId, onSwitchDatabase])

  useEffect(() => {
    if (databaseInfo?.databaseId) {
      setDbId(databaseInfo.databaseId)
    }
  }, [databaseInfo?.databaseId, setDbId])

  const logCtrl = useLogViewerController()

  const backupScheduleListCtls = useListController({ resource: 'backup-schedule', basePath: '' })

  const mdHidden = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  return (
    <Content 
      pageName={mode === 'metrics' ? "Database Metrics" : "Database Detail"}
      resourceName={mode === 'metrics' ? "Database Metrics" : "Database Detail"}
      breadcrumbs={options?.breadcrumbs}
      mainActions={
        <>
          {mode === 'metrics' &&
            <Button
              component={RouterLink}
              to={`${location?.pathname}`}
              size="small"
              variant="outlined"
            >
              Back to Detail
            </Button>
          }
          <MoreOptionsMenu
            databaseInfo={databaseInfo}
            permissions={permissions as any}
          />
        </>
      }
    >
      <LogViewerProvider value={logCtrl}>
        <Grid container spacing={4}>
          <Grid
            item
            xs={12}
          >
            <DatabaseSelectCard preSelectDbId={databaseInfo?.databaseId} onChange={handleChange} showDesc={true} />
          </Grid>
          {mode === 'detail' &&
            <>
              <Grid
                item
                md={6}
                xs={12}
              >
                {databaseInfo ? <DatabaseSummary databaseInfo={databaseInfo} /> : null}
              </Grid>
              {false && // backup schedule is not implemented yet
                <Grid
                  item
                  md={6}
                  xs={12}
                  >
                  <ListContextProvider value={backupScheduleListCtls}>
                    <DatabaseBackupSchedule />
                  </ListContextProvider>
                </Grid>
              }
              <Grid
                item
                md={6}
                xs={12}
                >
                {databaseInfo ? <DatabaseStatistics databaseInfo={databaseInfo} /> : null}
              </Grid>
              <Grid
              item
              md={6}
              xs={12}
              >
                <DatabaseTipsRecommend />
              </Grid>
            </>
          }
          {mode === 'metrics' &&
            <>  
              <Grid 
                item 
                md={timeRange === TimeRangeType.Year ? 12 : 6} 
                xs={12} 
              >
                {databaseInfo && <DatabaseSizeChart databaseInfo={databaseInfo} />}
              </Grid>
              <Grid 
                item 
                md={timeRange === TimeRangeType.Year ? 12 : 6} 
                xs={12} 
              >
                {databaseInfo && <TopRowCountChart databaseInfo={databaseInfo} />}
              </Grid>
              <Grid 
                item 
                md={timeRange === TimeRangeType.Year ? 12 : 6} 
                xs={12} 
              >
                {databaseInfo && <TopIndexSizeChart databaseInfo={databaseInfo} />}
              </Grid>
            </>
          }
        </Grid>
        <LogViewer />
      </LogViewerProvider>
    </Content>
  )
}


export default DatabaseDetail;




